import { CategoryNamesAndIds } from '@/pages/DiscoverCategories/constants/constant';

import { urlSafeTitle } from '../urls';

/**
 * Fail-soft fallback for top-cat slug → id resolution. category-api's `resolve-browse-url` is
 * authoritative and the loader prefers it; this map only carries the page when that call errors,
 * where the alternative is a search-response heuristic that goes wrong whenever the keyword
 * fallback's items sit under a different L1 (e.g. luxury watches filed under Jewelry).
 *
 * Do NOT treat it as ground truth. It falls through to `CategoryNamesAndIds`, which is keyed on
 * the Discover page's *display labels*, so a label can claim a slug that belongs to a different
 * category — `Coins` (the L2, 24) shadows the L3 `coins` (26475). The aliases below exist to
 * work around that collision, not to describe the taxonomy.
 */
const TOP_CATEGORY_SLUG_TO_ID: Readonly<Record<string, number>> = {
    'coins-currency-and-stamps': CategoryNamesAndIds.Coins,
    watches: 97,
};

/**
 * Resolves a top-level discover category slug (urlSafeTitle of display name) to category id.
 * Returns null when unknown — full browse resolution should use the category API.
 */
export const getTopCategoryIdFromSlug = (categorySlug: string): number | null => {
    const target = decodeURIComponent(categorySlug).toLowerCase();
    const fromAlias = TOP_CATEGORY_SLUG_TO_ID[target];
    if (fromAlias !== undefined) {
        return fromAlias;
    }
    for (const [name, id] of Object.entries(CategoryNamesAndIds)) {
        if (urlSafeTitle(name).toLowerCase() === target) {
            return id;
        }
    }
    return null;
};
